import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { cn } from '@/lib/utils'

interface PageTransitionProps {
    children: ReactNode
}

type Phase = 'idle' | 'leaving' | 'entering'

/** Soft fade between pages. The shared header stays put while the page underneath changes. */
export const PageTransition: FC<PageTransitionProps> = ({ children }): ReactNode => {
    const router = useRouter()
    const [phase, setPhase] = useState<Phase>('idle')

    useEffect(() => {
        let frame = 0
        let leaving = false

        const handleStart = (url: string, options?: { shallow?: boolean }) => {
            if (options?.shallow) return

            const nextPath = url.split(/[?#]/)[0]

            if (nextPath === window.location.pathname) return

            leaving = true
            setPhase('leaving')
        }

        const handleDone = () => {
            if (!leaving) return

            leaving = false
            setPhase('entering')
            window.cancelAnimationFrame(frame)
            frame = window.requestAnimationFrame(() => {
                frame = window.requestAnimationFrame(() => setPhase('idle'))
            })
        }

        router.events.on('routeChangeStart', handleStart)
        router.events.on('routeChangeComplete', handleDone)
        router.events.on('routeChangeError', handleDone)

        return () => {
            router.events.off('routeChangeStart', handleStart)
            router.events.off('routeChangeComplete', handleDone)
            router.events.off('routeChangeError', handleDone)
            window.cancelAnimationFrame(frame)
        }
    }, [router.events])

    return (
        <div
            id='page'
            className={cn(
                'transition-opacity',
                phase === 'idle' && 'opacity-100 duration-700 ease-soft',
                phase === 'leaving' && 'opacity-0 duration-200 ease-out',
                phase === 'entering' && 'opacity-0 duration-0'
            )}
        >
            {children}
        </div>
    )
}
